import _ from 'lodash'
import styled, { css } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AxiosResponse } from 'axios'

import { Flex } from '../../components/Flex'
import { brkmax } from '../../components/theme'
import { PriceVisibility } from '../PriceVisibility'
import { PriceLabel } from '../../components/PriceLabel'
import { Price } from '../../components/Price'
import { Button } from '../../components/Button'
import { Text } from '../../components/Text'
import { getPricesMode } from '../../redux/config'
import { getContractType } from '../../redux/user/selectors'
import { useApi, useLinkGenerator } from '../../hooks'
import { Routes } from '../../routes'
import { OrderItemStatusCode, OrderMetadata, getOrderItems, getOrderPrices } from '../../redux/order'
import { createTFunction } from '../../utils/helpers'
import { areAccountUserDataLoading, getAccountUserData } from '../../redux/account/user/selectors'
import { DialogActions } from '../../redux/dialog'

const CartPriceWrapper = styled(Flex)`
  justify-content: flex-end;
  margin-top: 44px;
  flex-wrap: wrap;

  .price-container {
    margin-right: 32px;
  }

  ${brkmax(
    1,
    css`
      margin-top: 0px;
      .price-container {
        margin-top: 20px;
        margin-right: 0px;
        width: 100%;
        justify-content: flex-end;
      }
      button {
        margin-top: 40px;
        flex: 1 1 auto;
      }
    `
  )}
`

export const CartPrice = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation(['cart'])
  const tc = createTFunction('cart:', t)
  const { generateRoute } = useLinkGenerator()
  const pricesMode = useSelector(getPricesMode)
  const contractType = useSelector(getContractType)
  const prices = useSelector(getOrderPrices)
  const items = useSelector(getOrderItems)
  const userData = useSelector(getAccountUserData)
  const { contracts } = userData.accounting
  const userDataLoading = useSelector(areAccountUserDataLoading)
  const allWithError = _.every(items, i => i.statusCode === OrderItemStatusCode.Error)
  const primaryContract = contracts === undefined ? undefined : contracts[0]
  const { request: orderMetaDataRequest, loading: orderMetaDataRequestRunning } = useApi()

  const handleGoTOCheckoutClick = async () => {
    const { reportPackages, isTrialSubscription } = primaryContract || {}

    if (!isTrialSubscription || !reportPackages?.[0]) {
      return navigate(generateRoute(Routes.Checkout))
    }

    const { consumedReports, totalReports } = reportPackages[0]
    const availableReports = _.toInteger(totalReports) - _.toInteger(consumedReports)
    try {
      const { data }: AxiosResponse<OrderMetadata> = await orderMetaDataRequest({
        url: '/api/ordering/order/meta',
        method: 'get'
      })

      if (data?.itemCount && data.itemCount > availableReports) {
        dispatch(
          DialogActions.showInfoDialog({
            title: tc('UpdateCartTitle'),
            text: tc('UpdateCartText')
          })
        )
      } else {
        navigate(generateRoute(Routes.Checkout))
      }
    } catch {
      dispatch(
        DialogActions.showInfoDialog({
          title: tc('CartErrorTitle'),
          text: tc('CartProcessingError')
        })
      )
    }
  }

  return (
    <CartPriceWrapper>
      <PriceVisibility>
        <Flex className="price-container">
          <Text color="primary" fontSize="24px" pr="16px" bold>
            {PriceLabel({ pricesMode, label: tc('TotalPrice'), netLabel: tc('TotalPriceSSTExcluded') })}
          </Text>
          <Text color="secondary" fontSize="24px" bold>
            {Price({
              pricesMode,
              contractType,
              grossValue: prices.totalValueWithTaxFormatted || '',
              netValue: prices.totalNetValueFormatted || ''
            })}
          </Text>
        </Flex>
      </PriceVisibility>

      <Button
        variant="secondary"
        disabled={_.isEmpty(items) || allWithError || orderMetaDataRequestRunning || userDataLoading}
        onClick={handleGoTOCheckoutClick}
      >
        {tc('GoToCheckout')}
      </Button>
    </CartPriceWrapper>
  )
}
